import { FC } from 'react';

import { FieldError } from 'react-hook-form';
import ReactSelect, { Props as SelectProps, StylesConfig } from 'react-select';

import useTheme from 'hooks/useTheme';
import { FilterStateParams, OptionType } from 'interfaces/shared.interface';
import { ThemeConfig } from 'interfaces/user.interface';

const DEFAULT_OPTION = { value: '10', label: '10' };

const getBackgroundColor = (theme: ThemeConfig, state: FilterStateParams): string => {
  if (theme === ThemeConfig.dark && state.isSelected) {
    return '#FDBA74';
  }
  if (theme === ThemeConfig.dark) {
    return 'black';
  }
  if (state.isSelected) {
    return '#FDBA74';
  }
  if (state.isFocused) {
    return 'rgba(123,136,159,0.25)';
  }

  return '';
};

const getHoverBackgroundColor = (theme: ThemeConfig, state: FilterStateParams): string => {
  if (theme === ThemeConfig.dark && state.isSelected) {
    return '#FDBA74';
  }
  if (theme === ThemeConfig.dark) {
    return '#18181B';
  }
  if (state.isSelected) {
    return '#FDBA74';
  }
  return 'rgba(123,136,159,0.25)';
};

const getBorderColor = (theme: ThemeConfig, state: FilterStateParams, error?: FieldError): string => {
  if (error) {
    return '#e53e3e';
  }
  if (theme === ThemeConfig.dark) {
    return '#CED4DA';
  }
  if (state.isFocused) {
    return '#F97316';
  }

  return '#d1d5db';
};

const getHoverBorderColor = (theme: ThemeConfig, state: FilterStateParams): string => {
  if (theme === ThemeConfig.dark) {
    return '#CBD5E1';
  }
  if (state.isFocused) {
    return '#F97316';
  }
  return '#d1d5db';
};

const getOptionColor = (theme: ThemeConfig): string => {
  return theme === ThemeConfig.dark ? 'white' : 'black';
};

export const getBackgroundColorForControl = (theme: ThemeConfig, isFilter?: boolean): string => {
  if (theme === ThemeConfig.light) {
    return 'white';
  }
  if (theme === ThemeConfig.dark) {
    return isFilter ? 'transparent' : 'black';
  }

  return 'transparent';
};

export const getSelectStyles = (
  theme: ThemeConfig,
  isMulti?: boolean,
  error?: FieldError,
  isAsync?: boolean,
  isClearable?: boolean
): StylesConfig<OptionType> => ({
  control: (provided, state) => ({
    ...provided,
    minHeight: '2.5rem',
    width: '100%',
    cursor: isAsync ? 'text' : 'pointer',
    fontSize: '0.875rem',
    borderStyle: 'solid',
    color: 'rgba(222,22,22,0.75)',
    borderColor: state.isFocused ? '#FF5733' : theme === ThemeConfig.dark ? '#41414B' : '#D9D9DE',
    outline: 'none',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: 'none',
    },
    boxShadow: 'none',
    '&:hover': {
      borderColor: '#FF5733',
    },
    backgroundColor: getBackgroundColorForControl(theme),
  }),
  option: (provided, state) => {
    const isDisabled = state.isDisabled;
    return {
      ...provided,
      fontSize: '0.875rem',
      backgroundColor: getBackgroundColor(theme, state),
      color: getOptionColor(theme),
      opacity: isDisabled ? '.65' : '1',
      margin: '4px',
      borderRadius: '4px',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        backgroundColor: isDisabled ? '' : getHoverBackgroundColor(theme, state),
      },
    };
  },
  menu: (provided) => ({
    ...provided,
    backgroundColor: theme === ThemeConfig.dark ? 'black' : 'white',
    fontSize: '0.875rem',
    zIndex: 20,
    border: `1px solid ${theme === ThemeConfig.dark ? '#41414B' : '#D9D9DE'}`,
  }),
  menuList: (provided) => ({
    ...provided,
    overflowX: 'hidden',
    paddingRight: '0.5rem',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: theme === ThemeConfig.dark || isMulti ? 'white' : 'rgba(50,51,52,0.25)',
    '&:hover': {
      color: theme === ThemeConfig.dark || isMulti ? 'white' : 'rgba(50,51,52,0.5)',
    },
    cursor: isAsync && !isMulti ? 'pointer' : '',
    display: isAsync && !isMulti ? 'none' : '',
    padding: '0 0.5rem',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: isClearable ? '0 10px' : '0',
    display: isAsync && isMulti ? 'none' : '',
    color: theme === ThemeConfig.dark || isMulti ? 'white' : 'rgba(50,51,52,0.25)',
    '&:hover': {
      color: theme === ThemeConfig.dark || isMulti ? 'white' : 'rgba(50,51,52,0.5)',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: theme === ThemeConfig.dark || isMulti ? 'white' : 'black',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    backgroundColor: theme === ThemeConfig.dark ? 'black' : 'white',
    borderRadius: isMulti || isClearable ? '0 2px 2px 0' : '',
    cursor: 'pointer',
    marginRight: '2px',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#EEEEF0',
    cursor: 'default',
    borderRadius: '4px',
    alignItems: 'center',
    fontSize: '1rem',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: theme === ThemeConfig.dark ? '#91939F' : '#5D5E6C',
    fontSize: '100%',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'transparent',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: theme === ThemeConfig.dark ? '#5D5E6C' : '#91939F',
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '20px',
  }),
  input: (provided) => ({
    ...provided,
    color: theme === ThemeConfig.dark ? 'white' : 'black',
    outline: 'none',
    boxShadow: 'none',
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '100%',
    overflowY: 'unset',
    borderColor: 'transparent',
    borderRadius: isAsync ? '0.125rem' : 'unset',
    borderBottomRightRadius: !isMulti && isAsync ? '0.125rem' : '0',
    borderTopRightRadius: !isMulti && isAsync ? '0.125rem' : '0',
    borderWidth: isAsync ? '1px' : '0',
    transitionDuration: '200ms',
    ':hover': {
      borderColor: 'transparent',
    },
  }),
});

const Select: FC<SelectProps<OptionType>> = ({ options, onChange, ...props }) => {
  const { theme } = useTheme();
  return (
    <ReactSelect
      id="react-select-component"
      styles={getSelectStyles(theme)}
      defaultValue={DEFAULT_OPTION}
      options={options}
      onChange={onChange}
      menuPlacement="top"
      maxMenuHeight={200}
      {...props}
    />
  );
};

export default Select;
